import { useState } from 'react';
import { Checkbox, FormControlLabel, FormGroup } from '@mui/material';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { uploadPblFile } from '@/api/pbl.api';
import { InputWrapper } from '@/components/Container/AuthContainer/styles';
import { errorHandler } from '@/utils/toastHandler';
import type { UpdateVolunteerForm, VolunteerFormState } from '../types';
import { FormPanel, FormSectionTitle, HelperText, SectionTitle } from '../styles';
import { Flex } from '@/layout';
import { Text } from '@/components/UI/Typography/Text';
import { theme } from '@/theme/Theme';
import { UploadFileField } from '../shared';
import { createLocalDocumentUrl, createPblUploadFormData, getUploadedPblFileId } from '../uploadedDocument';

interface Props {
  form: VolunteerFormState;
  errors: FieldErrors<VolunteerFormState>;
  control: Control<VolunteerFormState>;
  isTablet: boolean;
  updateForm: UpdateVolunteerForm;
}

const AuthorisationStep = ({ form, errors, control, isTablet, updateForm }: Props) => {
  //   @ts-ignore
  const { t } = useTranslation();
  const [selectedFileName, setSelectedFileName] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const selectAuthorisationFile = async (file: File) => {
    setSelectedFileName(file.name);
    updateForm('firm_authorisation_file_id', undefined);

    try {
      setIsUploading(true);
      const response = await uploadPblFile(createPblUploadFormData(file, 'lawyer_firm_authorisation'));
      const fileId = getUploadedPblFileId(response.data.data);

      if (response.data.success === false || typeof fileId !== 'number') {
        throw new Error(
          response.data.message ||
            t('pbl.lawyerRegistration.authorisation.errors.uploadFailed', {
              defaultValue: 'Failed to upload firm authorisation PDF.',
            })
        );
      }

      updateForm('firm_authorisation_file_id', fileId);
      updateForm('firm_authorisation_file', {
        file_id: fileId,
        name: file.name,
        url: createLocalDocumentUrl(file),
      });
    } catch (error: any) {
      setSelectedFileName('');
      updateForm('firm_authorisation_file', null);
      errorHandler(
        error.response?.data?.message ||
          error.message ||
          t('pbl.lawyerRegistration.authorisation.errors.uploadFailed', {
            defaultValue: 'Failed to upload firm authorisation PDF.',
          })
      );
    } finally {
      setIsUploading(false);
    }
  };

  const clearAuthorisationFile = () => {
    setSelectedFileName('');
    updateForm('firm_authorisation_file_id', undefined);
    updateForm('firm_authorisation_file', null);
  };

  return (
    <FormPanel isTablet={isTablet}>
      <SectionTitle>{t('pbl.lawyerRegistration.authorisation.section', { defaultValue: 'Section 5' })}</SectionTitle>
      <Flex gridGap={'5px'}>
        12.{' '}
        <FormSectionTitle>
          {t('pbl.lawyerRegistration.steps.authorisation', {
            defaultValue: 'Authorisation from Law Firm',
          })}
        </FormSectionTitle>
      </Flex>
      <Text>
        {t('pbl.lawyerRegistration.authorisation.intro', {
          defaultValue:
            'For the purposes of professional indemnity, volunteer lawyers participating in FreeLegalLink are required to have authorisation from their respective law firms that they are joining the Scheme to provide free legal advisory service to the public in their capacity of practising lawyers of their firms.',
        })}
      </Text>
      <Flex gridGap={10} alignItems="center" marginTop={20}>
        {t('pbl.lawyerRegistration.authorisation.downloadLabel', { defaultValue: 'Download:' })}
        <a
          href="https://www.ebram.org/download/Annex_1_Firm_authorisation_template.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Text color={theme.colors.primary500} style={{ textDecoration: 'underline', cursor: 'pointer' }}>
            {t('pbl.lawyerRegistration.authorisation.downloadTemplate', {
              defaultValue: 'Download Annex 1: Firm authorisation template',
            })}
          </Text>
        </a>
      </Flex>

      <FormGroup style={{ marginTop: 20 }}>
        <InputWrapper style={{ height: 'auto' }}>
          <UploadFileField
            name="firm_authorisation_file_id"
            clearAriaLabel={t('pbl.common.upload.clearFirmAuthorisationFile', {
              defaultValue: 'Clear firm authorisation file',
            })}
            fileName={selectedFileName || form.firm_authorisation_file?.name}
            isUploading={isUploading}
            onClear={clearAuthorisationFile}
            onSelectFile={selectAuthorisationFile}
            placeholder={t('pbl.lawyerRegistration.authorisation.uploadPlaceholder', {
              defaultValue: 'Upload signed authorisation form',
            })}
          />
          <Text marginTop={10} fontWeight={400} color={theme.colors.gray300}>
            {t('pbl.common.upload.pdfMaxSize10mb', { defaultValue: 'Upload as PDF. Max size 10MB' })}
          </Text>
        </InputWrapper>

        <Controller
          control={control}
          name="firm_authorisation_confirmed"
          rules={{
            validate: (value) =>
              value ||
              t('pbl.lawyerRegistration.authorisation.errors.confirmAuthorisation', {
                defaultValue: 'Please confirm law firm authorisation.',
              }),
          }}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={!!field.value} onChange={(event) => field.onChange(event.target.checked)} />}
              label={t('pbl.lawyerRegistration.authorisation.confirmAuthorisation', {
                defaultValue:
                  'I confirm that I have authorisation from my law firm, i.e. the law firm stated in paragraph 7 above, that I am joining FreelegalLink to provide free legal advisory service to the public in my capacity in the firm as stated in paragraph 8 above.',
              })}
            />
          )}
        />

        <Controller
          control={control}
          name="firm_authorisation_letter_attached"
          rules={{
            validate: (value) =>
              value ||
              t('pbl.lawyerRegistration.authorisation.errors.confirmLetterAttached', {
                defaultValue: 'Please confirm authorisation letter attachment.',
              }),
          }}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={!!field.value} onChange={(event) => field.onChange(event.target.checked)} />}
              label={t('pbl.lawyerRegistration.authorisation.confirmLetterAttached', {
                defaultValue:
                  'I attach the uploaded authorisation letter (in the form prescribed in Annex 1) signed by a partner of my firm.',
              })}
            />
          )}
        />

        <Controller
          control={control}
          name="law_firm_change_notify_agreed"
          rules={{
            validate: (value) =>
              value ||
              t('pbl.lawyerRegistration.authorisation.errors.agreeNotifyChange', {
                defaultValue: 'Please agree to notify law firm changes.',
              }),
          }}
          render={({ field }) => (
            <FormControlLabel
              control={<Checkbox checked={!!field.value} onChange={(event) => field.onChange(event.target.checked)} />}
              label={t('pbl.lawyerRegistration.authorisation.agreeNotifyChange', {
                defaultValue:
                  'I agree to inform FreelegalLink immediately upon any change of law firm from the one stated in paragraph 7 above.',
              })}
            />
          )}
        />
      </FormGroup>

      {(errors.firm_authorisation_confirmed?.message ||
        errors.firm_authorisation_letter_attached?.message ||
        errors.law_firm_change_notify_agreed?.message ||
        errors.firm_authorisation_file_id?.message) && (
        <HelperText style={{ color: theme.colors.danger }}>
          {errors.firm_authorisation_confirmed?.message ||
            errors.firm_authorisation_letter_attached?.message ||
            errors.law_firm_change_notify_agreed?.message ||
            errors.firm_authorisation_file_id?.message}
        </HelperText>
      )}
    </FormPanel>
  );
};

export default AuthorisationStep;
